import "./SupportingDocuments.scss";
import React, { useEffect, useState } from "react";
import { documentPurposes } from "@quest-finance/quest-fe-shared/dist/application";
import { Collapse, Tabs, TabsProps } from "antd";
import { useSelector } from "react-redux";
import { ApplicationDocuments } from "../../../application";
import { Uploader } from "../../../document";
import { QfCollapse } from "../../../theme";
import {
  DocumentList as WorkflowDocument,
  workflowSelectors,
} from "../../../workflow";
import { assessmentResponseSelector } from "../../selectors/assessmentDetail";
import { DocumentList } from "../../types/Document";
import AssessmentDocuments from "../assessment-documents/AssessmentDocuments";

const { Panel } = Collapse;

type SupportingDocumentsProps = {
  defaultActiveTab?: DocumentList;
};

const SupportingDocuments: React.FC<SupportingDocumentsProps> = ({
  defaultActiveTab = DocumentList.INTERNAL,
}) => {
  const [lastUpload, setLastUpload] = useState(0);
  const creditWorkflow = useSelector(workflowSelectors.creditWorkflowSelector);
  const assessment = useSelector(assessmentResponseSelector);
  const [activeTab, setActiveTab] = useState<DocumentList>(defaultActiveTab);
  const [uploadPath, setUploadPath] = useState(
    `/assessment/assessments/${assessment.id}/documents`
  );
  const [documentPurpose, setDocumentPurpose] =
    useState<documentPurposes.DOCUMENT_PURPOSES>(
      documentPurposes.DOCUMENT_PURPOSES.INTERNAL_DOCUMENT
    );

  useEffect(() => {
    let docPurpose = "";
    let path = `/application/applications/${assessment.externalId}/documents/upload`;

    if (activeTab === DocumentList.INTERNAL) {
      docPurpose = documentPurposes.DOCUMENT_PURPOSES.INTERNAL_DOCUMENT;
      path = `/assessment/assessments/${assessment.id}/documents`;
    } else if (activeTab === DocumentList.SUPPORTING) {
      docPurpose = documentPurposes.DOCUMENT_PURPOSES.SUPPORTING_DOCUMENT;
    } else if (activeTab === DocumentList.SETTLEMENT) {
      docPurpose = documentPurposes.DOCUMENT_PURPOSES.SETTLEMENT_DOCUMENT;
    }

    setDocumentPurpose(docPurpose as documentPurposes.DOCUMENT_PURPOSES);
    setUploadPath(path);
  }, [assessment, activeTab]);

  const onTabChange = (key: string) => {
    setActiveTab(key as DocumentList);
  };

  const onUploadComplete = (numOfFileUploaded: number) => {
    if (numOfFileUploaded) setLastUpload(Date.now());
  };

  const tabItems: TabsProps["items"] = [
    {
      key: DocumentList.INTERNAL,
      label: <span data-testid="internal-doc-tab">Internal</span>,
      children: (
        <AssessmentDocuments
          assessmentId={assessment.id}
          lastUpload={lastUpload}
        />
      ),
    },
    {
      key: DocumentList.SUPPORTING,
      label: <span data-testid="supporting-doc-tab">Supporting</span>,
      children: (
        <ApplicationDocuments
          applicationId={assessment.externalId}
          purpose={documentPurposes.DOCUMENT_PURPOSES.SUPPORTING_DOCUMENT}
          lastUpload={lastUpload}
        />
      ),
    },
    {
      key: DocumentList.SETTLEMENT,
      label: <span data-testid="settlement-doc-tab">Settlement</span>,
      children: (
        <ApplicationDocuments
          applicationId={assessment.externalId}
          purpose={documentPurposes.DOCUMENT_PURPOSES.SETTLEMENT_DOCUMENT}
          lastUpload={lastUpload}
        />
      ),
    },
  ].concat(
    creditWorkflow.id > 0
      ? [
          {
            key: DocumentList.WORKFLOW,
            label: <span data-testid="workflow-doc-tab">Workflow</span>,
            children: <WorkflowDocument />,
          },
        ]
      : []
  );

  return (
    <QfCollapse
      defaultActiveKey={["1"]}
      className="supporting-documents"
      expandIconPosition="end"
      ghost
    >
      <Panel
        key="1"
        header={<div className="section-header-1">Supporting Documents</div>}
      >
        <Uploader
          uploadPath={uploadPath}
          documentPurpose={documentPurpose}
          onUploadComplete={onUploadComplete}
          disabled={activeTab !== DocumentList.INTERNAL}
          testid="document-uploader"
        />
        <Tabs
          activeKey={activeTab}
          items={tabItems}
          onChange={onTabChange}
          className="documents-tabs"
        />
      </Panel>
    </QfCollapse>
  );
};

export default SupportingDocuments;
